import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@shared/services/api';
import { queryKeys } from '@shared/lib/queryKeys';
import { CollaboratorPermission } from '@shared/types';
import { toast } from '@shared/lib/toast';

// Shared-with-me notes and collaborator management are online-only — shared
// notes live on the server (they belong to other users), never in the
// offline-first local store.

/** Notes shared with the caller + a note_id → best-permission map. */
export const useSharedNotesQuery = (enabled = true) =>
  useQuery({
    queryKey: queryKeys.notes.shared(),
    queryFn: () => api.getSharedNotes(),
    staleTime: 30 * 1000,
    retry: 1,
    enabled,
  });

export const useCollaboratorsQuery = (noteId: string | null) =>
  useQuery({
    queryKey: queryKeys.notes.collaborators(noteId ?? ''),
    queryFn: () => api.getCollaborators(noteId as string),
    enabled: !!noteId,
    staleTime: 30 * 1000,
    retry: 1,
  });

export const useAddCollaboratorMutation = (noteId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { email?: string; org_id?: string; permission: CollaboratorPermission }) =>
      api.addCollaborator(noteId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.notes.collaborators(noteId) });
      toast.success('Note shared');
    },
    onError: (error: unknown) => {
      const resp = (error as { response?: { status?: number; data?: { error?: string } } })?.response;
      if (resp?.status === 404) {
        toast.error("No Hintword account with that email — ask them to sign up first at hintword.com.");
      } else if (resp?.status === 409) {
        toast.error('Already shared with them.');
      } else if (typeof resp?.data?.error === 'string') {
        // Surface the backend's own message (e.g. "cannot share a note with yourself")
        toast.error(resp.data.error);
      } else {
        toast.error(error instanceof Error ? error.message : 'Failed to share note');
      }
    },
  });
};

export const useUpdateCollaboratorMutation = (noteId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ collaboratorId, permission }: { collaboratorId: string; permission: CollaboratorPermission }) =>
      api.updateCollaborator(noteId, collaboratorId, permission),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.notes.collaborators(noteId) });
      toast.success('Permission updated');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to update permission');
    },
  });
};

export const useRemoveCollaboratorMutation = (noteId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (collaboratorId: string) => api.removeCollaborator(noteId, collaboratorId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.notes.collaborators(noteId) });
      toast.success('Access removed');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to remove access');
    },
  });
};
